import { useEffect, useRef, useState } from 'react';
import { logTtsEvent, getStopToken, getActiveSessionId } from '../utils/ttsController';
import { speakVerses, stopSpeaking } from '../utils/tts';
import { updateMediaSession } from '../utils/mediaSession';
import { saveTtsProgress, loadTtsProgress, clearTtsProgress } from '../utils/ttsProgress';
import { segmentPageContent } from '../utils/textSegmenter';
import { loadSettings } from '../settings/voiceSettings';

interface UseTtsAutoAdvanceProps {
    isPlaying: boolean;
    setIsPlaying: (b: boolean) => void;
    pageIndex: number;
    pages: string[][];
    verseData: Record<string, string> | null;
    isPaginating: boolean;
    advancePageOrChapter: (reason: 'auto' | 'manual') => boolean;
    onToast: (msg: string) => void;
    setIsBuffering?: (b: boolean) => void;
    bookId: string;
    chapter: number;
    corpusId: string;
}

export function useTtsAutoAdvance({
    isPlaying,
    setIsPlaying,
    pageIndex,
    pages,
    verseData,
    isPaginating,
    advancePageOrChapter,
    onToast,
    setIsBuffering,
    bookId,
    chapter,
    corpusId
}: UseTtsAutoAdvanceProps) {
    const [pendingAdvance, setPendingAdvance] = useState(false);

    // Refs for Deduplication
    const lastAutoAdvanceKeyRef = useRef<string>('');
    const advanceInFlightRef = useRef(false);

    // Track active sentences to resume if needed during same session
    const currentSentencesRef = useRef<string[]>([]);

    // Effect: Handle Auto-Reading when pendingAdvance is true and content is ready
    useEffect(() => {
        if (!pendingAdvance) return;

        if (!isPlaying) {
            setPendingAdvance(false);
            return;
        }

        if (isPaginating || !verseData || !pages[pageIndex]) {
            return;
        }

        const verses = pages[pageIndex].map(v => verseData[v] || '');
        if (verses.length === 0) {
            setPendingAdvance(false);
            return;
        }

        setPendingAdvance(false);
        doSpeak(verses, true); // Auto-advance always starts from 0 (forceFresh)

    }, [pendingAdvance, isPlaying, isPaginating, verseData, pageIndex]);

    const requestAutoAdvance = () => {
        const sessionId = getActiveSessionId();
        const currentRefKey = `${sessionId}:${pageIndex}`;

        logTtsEvent('AUTO_ADVANCE_REQUESTED', { sessionId, pageIndex, reason: 'tts_complete' });

        if (advanceInFlightRef.current) return;
        if (lastAutoAdvanceKeyRef.current === currentRefKey) return;

        let autoEnabled = false;
        try {
            const s = localStorage.getItem('claranarratio.voiceSettings.v1');
            const conf = s ? JSON.parse(s) : {};
            autoEnabled = !!conf.autoPageFlip;
        } catch { }

        if (!autoEnabled) {
            logTtsEvent('AUTO_ADVANCE_SKIPPED', { reason: 'autoAdvanceOff' });
            setIsPlaying(false);
            return;
        }

        advanceInFlightRef.current = true;
        lastAutoAdvanceKeyRef.current = currentRefKey;

        const success = advancePageOrChapter('auto');

        if (success) {
            logTtsEvent('AUTO_ADVANCE_COMMITTED', { from: pageIndex, to: pageIndex + 1, sessionId });
            setPendingAdvance(true);
            setIsPlaying(true);
        } else {
            logTtsEvent('AUTO_ADVANCE_SKIPPED', { reason: 'noNextPage' });
            setIsPlaying(false);
            onToast("Finished reading.");
        }

        setTimeout(() => {
            advanceInFlightRef.current = false;
        }, 500);
    };

    const doSpeak = (verses: string[], forceFresh: boolean = false) => {
        setIsPlaying(true);
        const startToken = getStopToken();

        // 1. Segment
        const sentences = segmentPageContent(verses);
        currentSentencesRef.current = sentences;

        // 2. Determine Start Index
        let startIndex = 0;
        if (!forceFresh) {
            const saved = loadTtsProgress(corpusId, bookId);
            if (saved && saved.chapter === chapter && saved.pageIndex === pageIndex) {
                // Resume!
                startIndex = saved.chunkIndex;
                console.log(`[TTS] Resuming at index ${startIndex}/${sentences.length}`);
            }
        } else {
            // New page or forced restart, clear old progress for this book to avoid confusion
            clearTtsProgress(corpusId, bookId);
        }

        // Sanity check
        if (startIndex >= sentences.length) startIndex = 0;

        updateMediaSession('playing', {
            title: `${bookId} ${chapter}`,
            artist: 'ClaraNarratio'
        }, {
            onPause: () => stop('media_pause'),
            onStop: () => stop('media_stop'),
            onPlay: () => play(verses),
        });

        const onComplete = (err?: any) => {
            const currentToken = getStopToken();
            if (currentToken !== startToken) return;

            if (err) {
                console.error("[TTS] Page Error", err);
                setIsPlaying(false);
                return;
            }

            // Clear progress on completion (so we don't stick to end)
            clearTtsProgress(corpusId, bookId);
            requestAutoAdvance();
        };

        const onProgress = (index: number, text: string) => {
            // Save Progress
            const settings = loadSettings();
            saveTtsProgress({
                corpusId,
                bookId,
                chapter,
                pageIndex,
                chunkIndex: index,
                snippet: text.slice(0, 60),
                timestamp: Date.now(),
                provider: settings.provider
            });
        };

        speakVerses(sentences, {
            onEnd: onComplete,
            onProgress,
            onStatusChange: (status) => {
                if (setIsBuffering) setIsBuffering(status === 'buffering');
            },
            startIndex
        });
    };

    const play = (verses: string[]) => {
        setPendingAdvance(false);
        // Play implies resume if available, so forceFresh = false
        doSpeak(verses, false);
    };

    const restart = (verses: string[]) => {
        setPendingAdvance(false);
        clearTtsProgress(corpusId, bookId);
        doSpeak(verses, true);
    };

    const stop = (reason?: string) => {
        setPendingAdvance(false);
        stopSpeaking(reason);
        setIsPlaying(false);
        advanceInFlightRef.current = false;
        // NOTE: We do NOT clear ttsProgress here, allowing resume.
        updateMediaSession('paused');
    };

    return {
        play,
        restart,
        stop,
        isPending: pendingAdvance
    };
}
